import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { speak, playClickSound } from '../utils/speech';

interface Item { arabic: string; english: string; emoji: string; hex?: string; num?: string; }
interface Props { title: string; arabicTitle: string; items: Item[]; onBack: () => void; color?: string; }

export const GridLesson: React.FC<Props> = ({ title, arabicTitle, items, onBack, color = '#4ECDC4' }) => {
  const [active, setActive] = useState<number | null>(null);

  const handleTap = (i: number, item: Item) => {
    playClickSound();
    setActive(active === i ? null : i);
    speak(item.arabic, 'ar', 0.8);
    setTimeout(() => speak(item.english, 'en', 1), 1200);
  };

  return (
    <div style={{ minHeight: '100vh', padding: '20px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={onBack}
            style={{ background: 'white', border: `3px solid ${color}`, borderRadius: '50px', padding: '10px 20px', cursor: 'pointer', color, fontWeight: 700 }}>
            ← Back
          </motion.button>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", color, fontSize: '1.8rem' }}>
            {title} — {arabicTitle}
          </h2>
        </div>
        <p style={{ textAlign: 'center', color: '#636E72', marginBottom: '20px' }}>
          Tap any card to hear it! 🔊
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '15px' }}>
          {items.map((item, i) => (
            <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.05, type: 'spring' }}
              whileHover={{ scale: 1.08, y: -5 }} whileTap={{ scale: 0.95 }}
              onClick={() => handleTap(i, item)}
              style={{
                background: item.hex || (active === i ? color : 'white'),
                borderRadius: '20px', padding: '20px 15px', textAlign: 'center', cursor: 'pointer',
                boxShadow: active === i ? `0 10px 30px ${color}50` : '0 4px 15px rgba(0,0,0,0.08)',
                border: `3px solid ${active === i ? color : 'transparent'}`,
                transition: 'all 0.2s'
              }}>
              <motion.div animate={active === i ? { scale: [1, 1.3, 1], rotate: [0, 10, -10, 0] } : {}}
                transition={{ duration: 0.4 }}
                style={{ fontSize: '45px', marginBottom: '8px' }}>
                {item.emoji}
              </motion.div>
              {item.num && <div style={{ fontSize: '2rem', fontWeight: 900, color: item.hex ? '#333' : color }}>{item.num}</div>}
              <div style={{
                fontSize: '1.2rem', direction: 'rtl', fontWeight: 700,
                color: item.hex ? (item.hex === '#DFE6E9' ? '#666' : 'white') : (active === i ? 'white' : '#2D3436')
              }}>
                {item.arabic}
              </div>
              <div style={{
                fontSize: '0.85rem', marginTop: '4px',
                color: item.hex ? (item.hex === '#DFE6E9' ? '#888' : 'rgba(255,255,255,0.8)') : (active === i ? 'rgba(255,255,255,0.8)' : '#636E72')
              }}>
                {item.english}
              </div>
              {active === i && (
                <div style={{ fontSize: '0.75rem', color: item.hex ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.7)', marginTop: '5px' }}>
                  🔊 listening...
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
